<script setup lang="ts">
import { AUTH_SCREEN_COPY, checkEmailAddress } from '@zerohuman/shared'
import { Button, Input, Label, PROVIDER_MARK_NAMES, Separator, ZhProviderMark, ZhSystemLabel } from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref } from 'vue'
import { api, type SignInWay } from '@/api'
import { codeRefused } from '@/lib/signInError'

/**
 * The sign-in screen's ways in (#636): a button for each provider this deployment has set up — Google, Microsoft,
 * GitHub — and an email address with a six-digit code sent to it. No passwords.
 *
 * Quiet (#1933): the providers are equal-weight outline buttons, each with the provider's mark, in a fixed order;
 * then an "or" divider and the email, whose "Continue with email" is the one filled button on the page. In the code
 * step that one is "Sign in". How each way authenticates is unchanged.
 *
 * The provider buttons are plain links to the API, which runs the round trip and comes back with a session. The
 * code is two steps here: ask for it, then type it back, after which the page reloads into the session.
 *
 * The code step (#2121) can ask for a new code for the same address ("Send a new code", outline, so "Sign in" stays
 * the one filled button) and says when it went. The button doing the work says so ("Sending...", "Signing in...")
 * while every button of the step is disabled, and a correct code says "Signed in, opening your workspace" until the
 * page reloads. An error sits in the form it is about, under its field, and the field points to it
 * (`aria-describedby`) so a screen reader announces it with the field. The field is marked `aria-invalid` only when
 * what was typed in it was refused: a refused resend is not about the code box. A refused code adds a pointer to
 * "Send a new code"; a code check that failed for another reason (no account, a server fault, no connection) gets
 * neither, since a new code would fail the same way.
 */
type ProviderWay = Exclude<SignInWay, 'email'>

const NAMES: Record<ProviderWay, string> = {
    google: 'Google',
    microsoft: 'Microsoft',
    github: 'GitHub',
}
/** Until the API says otherwise: GitHub, as it always was, and the email code, which needs nothing set up. */
const ways = ref<SignInWay[]>(['github', 'email'])
const step = ref<'email' | 'code'>('email')
const email = ref('')
/** The address the code step's codes go to: what was typed, trimmed, once a code was sent to it. */
const sentTo = ref('')
const code = ref('')
/**
 * The action in flight, so the button doing it can say so. A correct code leaves it at `verify`: the page is
 * reloading, so nothing on it should be pressed again.
 */
const pending = ref<'send' | 'resend' | 'verify' | null>(null)
const busy = computed(() => pending.value !== null)
const error = ref<string | null>(null)
/**
 * Which action the current error came from. Only a refused code is about what is in the code box, so only that
 * marks it invalid; a refused "Send a new code" is still announced with the box, but does not call it wrong. A code
 * check that failed for any other reason (`codeRefused` is false) leaves it unset: the error is still announced
 * with the box, without the invalid mark or the pointer to a new code.
 */
const failedAction = ref<'send' | 'resend' | 'verify' | null>(null)
/** A status line in the code step: a new code went, or the code was accepted. */
const notice = ref<string | null>(null)

/** The ids an error line takes in each step, so that step's field can point to it. */
const ERROR_IDS = { email: 'sign-in-email-error', code: 'sign-in-code-error' } as const
/**
 * Said after a refused code check, inside the error so it is announced with the code box. Five wrong tries spend
 * every live code for the address, after which even the right one is refused: a new code is the way out, and older
 * codes are not cancelled, so the newest is the one sure to work.
 */
const WRONG_CODE_HINT = 'Still not working? Send a new code and use the newest one.'

/** Clears the error and status line, as every new action and leaving the code step do. */
function clearFeedback(): void {
    error.value = null
    failedAction.value = null
    notice.value = null
}

/** The approved order (the kit's), whatever order the API lists the ways in. */
const providerWays = computed(() => PROVIDER_MARK_NAMES.filter(way => ways.value.includes(way)))
const offersEmail = computed(() => ways.value.includes('email'))

onMounted(async () => {
    try {
        const answer = await api.signInWays()
        if (Array.isArray(answer?.ways) && answer.ways.length > 0) {
            ways.value = answer.ways
        }
    } catch {
        // An API older than #636 has no list: the defaults above are what it offers.
    }
})

/**
 * Asks for a code, once the address is one (#1721). Any address will do, personal ones included; a malformed or
 * empty one is answered here, in the API's own words, and no code is asked for.
 */
async function sendCode(): Promise<void> {
    if (busy.value) return
    const address = checkEmailAddress(email.value)
    if (!address.ok) {
        error.value = address.message
        failedAction.value = 'send'
        return
    }
    if (await requestCode(address.email, 'send')) {
        step.value = 'code'
    }
}

/** "Send a new code" (#2121): another code to the address the step is for, without typing it again. */
async function resendCode(): Promise<void> {
    if (busy.value) return
    if (await requestCode(sentTo.value, 'resend')) {
        notice.value = `We sent a new code to ${sentTo.value}.`
    }
}

/**
 * The send path both steps share: asks for a code for the address, with `action`'s button saying so meanwhile. On
 * a refusal the API's own words are the error and the step stays as it was. True when a code went.
 */
async function requestCode(address: string, action: 'send' | 'resend'): Promise<boolean> {
    pending.value = action
    clearFeedback()
    await nextTick()
    try {
        await api.requestSignInCode(address)
        sentTo.value = address
        code.value = ''
        return true
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'We could not send a code. Try again.'
        failedAction.value = action
        return false
    } finally {
        pending.value = null
    }
}

async function verify(): Promise<void> {
    if (busy.value) return
    pending.value = 'verify'
    clearFeedback()
    await nextTick()
    try {
        await api.verifySignInCode(sentTo.value, code.value.trim())
        // The session cookie came back with the answer: reload into it, as a provider's round trip does. Still busy,
        // so nothing is pressed again while the page reloads, and saying so in the meantime.
        notice.value = 'Signed in, opening your workspace'
        window.location.assign('/')
    } catch (caught) {
        // The API's wording gives no advice (it is shared with screens that have no resend); this screen adds its own
        // pointer to "Send a new code" under the error (`WRONG_CODE_HINT`), but only when the code itself was refused.
        error.value = caught instanceof Error ? caught.message : 'That code did not work.'
        failedAction.value = codeRefused(caught) ? 'verify' : null
        pending.value = null
    }
}

/** Back to the address form, with the address that was typed still in it. */
function useAnotherAddress(): void {
    step.value = 'email'
    code.value = ''
    clearFeedback()
}
</script>

<template>
	<div class="flex w-full flex-col gap-6" data-testid="sign-in-panel">
		<div v-if="providerWays.length > 0" class="flex flex-col gap-3">
			<Button
				v-for="way in providerWays"
				:key="way"
				as="a"
				variant="outline"
				class="w-full"
				:href="`/v1/auth/${way}`"
				:data-testid="`sign-in-${way}`"
			>
				<ZhProviderMark :provider="way" />
				Continue with {{ NAMES[way] }}
			</Button>
		</div>

		<div v-if="offersEmail && providerWays.length > 0" class="flex items-center gap-3">
			<Separator class="flex-1" />
			<ZhSystemLabel as="span" tone="muted">or</ZhSystemLabel>
			<Separator class="flex-1" />
		</div>

		<!-- novalidate: the browser's own message would stand in for ours, so the address is checked on submit. -->
		<form
			v-if="offersEmail && step === 'email'"
			class="flex flex-col gap-3"
			data-testid="sign-in-email-form"
			novalidate
			@submit.prevent="sendCode"
		>
			<Label for="sign-in-email">Email</Label>
			<Input
				id="sign-in-email"
				v-model="email"
				type="email"
				autocomplete="email"
				placeholder="you@company.com"
				required
				:aria-invalid="error ? 'true' : undefined"
				:aria-describedby="error ? ERROR_IDS.email : undefined"
				data-testid="sign-in-email"
			/>
			<p
				v-if="error"
				:id="ERROR_IDS.email"
				class="text-sm text-destructive"
				role="alert"
				data-testid="sign-in-error"
			>
				{{ error }}
			</p>
			<Button type="submit" class="w-full" :disabled="busy" data-testid="sign-in-send-code">
				{{ pending === 'send' ? 'Sending...' : 'Continue with email' }}
			</Button>
			<p class="text-sm text-muted-foreground">{{ AUTH_SCREEN_COPY.emailHelper }}</p>
		</form>

		<form
			v-else-if="offersEmail && step === 'code'"
			class="flex flex-col gap-3"
			data-testid="sign-in-code-form"
			@submit.prevent="verify"
		>
			<Label for="sign-in-code">We sent a six-digit code to {{ sentTo }}. It works once, for 10 minutes.</Label>
			<Input
				id="sign-in-code"
				v-model="code"
				inputmode="numeric"
				autocomplete="one-time-code"
				maxlength="6"
				placeholder="123456"
				required
				:aria-invalid="error && failedAction === 'verify' ? 'true' : undefined"
				:aria-describedby="error ? ERROR_IDS.code : undefined"
				data-testid="sign-in-code"
			/>
			<p
				v-if="error"
				:id="ERROR_IDS.code"
				class="text-sm text-destructive"
				role="alert"
				data-testid="sign-in-error"
			>
				{{ error }}
				<span v-if="failedAction === 'verify'" data-testid="sign-in-code-hint">{{ WRONG_CODE_HINT }}</span>
			</p>
			<p v-if="notice" class="text-sm text-muted-foreground" role="status" data-testid="sign-in-status">
				{{ notice }}
			</p>
			<Button type="submit" class="w-full" :disabled="busy" data-testid="sign-in-verify">
				{{ pending === 'verify' ? 'Signing in...' : 'Sign in' }}
			</Button>
			<Button
				type="button"
				variant="outline"
				class="w-full"
				:disabled="busy"
				data-testid="sign-in-resend-code"
				@click="resendCode"
			>
				{{ pending === 'resend' ? 'Sending...' : 'Send a new code' }}
			</Button>
			<Button type="button" variant="ghost" size="sm" :disabled="busy" @click="useAnotherAddress">
				Use another address
			</Button>
		</form>
	</div>
</template>
